// ============================================
// ACCIONESARCHIVADO.JS
// ============================================
// Botones "Restaurar" y "Borrar permanente" para tarjetas en la pestaña
// "Archivados" del módulo Clientes. Solo visibles para rol 'direccion'.
// El "Borrar permanente" es destructivo: pide confirmación más estricta y
// llama a DELETE /:tipo/:id/permanente con validación de hijos.

import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import { useUsuario } from '../context/UsuarioContext';
import { API_BASE_CLI } from './helpersClientes';

const FF = "system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif";

const TIPO_META = {
  grupo:        { path: 'grupos',           label: 'grupo (cliente)' },
  razon_social: { path: 'razones-sociales', label: 'razón social' },
  sucursal:     { path: 'sucursales',       label: 'sucursal' },
  persona:      { path: 'personas',         label: 'persona' },
};

const DETALLE_HUMANO = {
  razones_sociales:           (n) => `${n} razon${n === 1 ? '' : 'es'} sociales`,
  sucursales:                 (n) => `${n} sucursal${n === 1 ? '' : 'es'}`,
  sucursales_huerfanas:       (n) => `${n} sucursal${n === 1 ? '' : 'es'} huérfana${n === 1 ? '' : 's'}`,
  oportunidades:              (n) => `${n} expediente${n === 1 ? '' : 's'}`,
  roles_contacto:             (n) => `${n} rol${n === 1 ? '' : 'es'} de contacto`,
};

function esRolDireccion(usuario) {
  const r = usuario?.rol;
  if (!r || typeof r !== 'string') return false;
  const norm = r.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
  return norm === 'direccion';
}

export default function AccionesArchivado({ tipo, id, entidad, onCambio }) {
  const { usuario } = useUsuario();
  const [accion, setAccion] = useState(null); // null | 'restaurar' | 'permanente'
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState(null);
  const [bloqueo, setBloqueo] = useState(null);
  const [toast, setToast] = useState(null);

  const meta = TIPO_META[tipo];
  const visible = esRolDireccion(usuario) && meta;
  if (!visible) return null;

  const cerrar = () => {
    if (enviando) return;
    setAccion(null);
    setError(null);
    setBloqueo(null);
  };

  const ejecutar = async () => {
    setEnviando(true);
    setError(null);
    setBloqueo(null);
    try {
      const token = localStorage.getItem('beep_token') || '';
      const url = accion === 'restaurar'
        ? `${API_BASE_CLI}/api/clientes/${meta.path}/${id}/restaurar`
        : `${API_BASE_CLI}/api/clientes/${meta.path}/${id}/permanente`;
      const method = accion === 'restaurar' ? 'POST' : 'DELETE';
      const r = await fetch(url, {
        method,
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      const data = await r.json().catch(() => ({}));
      if (r.status === 401 || r.status === 403) {
        setError('No tienes permisos para esta acción');
        return;
      }
      if (r.status === 409 && data?.error === 'NO_PUEDE_BORRAR') {
        setBloqueo({ detalle: data.detalle || {} });
        return;
      }
      if (r.status === 409 && data?.error === 'NO_ARCHIVADO') {
        setError(data.mensaje || 'No se puede ejecutar esta acción');
        return;
      }
      if (!r.ok || !data?.ok) {
        throw new Error(data?.error || `Error ${r.status}`);
      }
      setAccion(null);
      setToast({
        tipo: 'ok',
        msg: accion === 'restaurar' ? 'Restaurado' : 'Eliminado permanentemente',
      });
      setTimeout(() => {
        if (onCambio) onCambio();
      }, 1000);
    } catch (e) {
      setError(e.message || 'Error inesperado');
    } finally {
      setEnviando(false);
    }
  };

  const onClickRestaurar = (e) => { e.stopPropagation(); setAccion('restaurar'); };
  const onClickPermanente = (e) => { e.stopPropagation(); setAccion('permanente'); };

  return (
    <>
      <div style={{ display: 'flex', gap: 6, flexShrink: 0 }} onClick={(e) => e.stopPropagation()}>
        <button
          onClick={onClickRestaurar}
          title={`Restaurar ${meta.label}`}
          aria-label={`Restaurar ${meta.label}`}
          style={{
            background: 'transparent',
            color: '#16a34a',
            border: '1px solid transparent',
            borderRadius: 8,
            width: 28, height: 28,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 14, lineHeight: 1, padding: 0,
            cursor: 'pointer', fontFamily: FF,
            transition: 'background 0.15s, border-color 0.15s',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = '#f0fdf4';
            e.currentTarget.style.borderColor = '#bbf7d0';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'transparent';
            e.currentTarget.style.borderColor = 'transparent';
          }}
        >↩</button>
        <button
          onClick={onClickPermanente}
          title={`Borrar permanentemente ${meta.label}`}
          aria-label={`Borrar permanentemente ${meta.label}`}
          style={{
            background: '#dc2626', color: '#fff',
            border: 'none', borderRadius: 8,
            width: 28, height: 28,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 14, lineHeight: 1, padding: 0,
            cursor: 'pointer', fontFamily: FF,
            transition: 'background 0.15s',
          }}
          onMouseEnter={(e) => { e.currentTarget.style.background = '#b91c1c'; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = '#dc2626'; }}
        >🗑️</button>
      </div>

      {accion && (
        <ModalConfirmar
          accion={accion}
          tipo={tipo} meta={meta} entidad={entidad}
          enviando={enviando}
          error={error}
          bloqueo={bloqueo}
          onCerrar={cerrar}
          onConfirmar={ejecutar}
        />
      )}

      {toast && ReactDOM.createPortal(
        <div style={{
          position: 'fixed', bottom: 28, left: '50%', transform: 'translateX(-50%)',
          background: '#16a34a', color: '#fff', padding: '12px 22px', borderRadius: 10,
          fontSize: 14, fontWeight: 600, fontFamily: FF, zIndex: 6500,
          boxShadow: '0 8px 24px rgba(0,0,0,0.18)',
        }}>{toast.msg}</div>,
        document.body
      )}
    </>
  );
}

function ModalConfirmar({ accion, meta, entidad, enviando, error, bloqueo, onCerrar, onConfirmar }) {
  const nombre = entidad?.nombre || entidad?.nombre_completo || entidad?.razon_social
    || entidad?.nombre_interno || `${meta.label} #${entidad?.id}`;

  const esPermanente = accion === 'permanente';

  const contenido = (
    <div
      onClick={(e) => e.stopPropagation()}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)',
        zIndex: 6000, display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 20, fontFamily: FF,
      }}
    >
      <div onClick={(e) => e.stopPropagation()}
        style={{
          background: '#fff', borderRadius: 14, width: '100%', maxWidth: 480,
          boxShadow: '0 20px 50px rgba(0,0,0,0.3)', overflow: 'hidden',
        }}
      >
        <div style={{ padding: '20px 22px 12px' }}>
          <h3 style={{
            margin: 0, color: esPermanente ? '#b91c1c' : '#270f2b',
            fontSize: 17, fontWeight: 700, fontFamily: FF,
          }}>
            {esPermanente ? `Borrar permanentemente este ${meta.label}?` : `Restaurar este ${meta.label}?`}
          </h3>
          <div style={{ marginTop: 8, fontSize: 14, color: '#5e666e', lineHeight: 1.5 }}>
            <strong style={{ color: '#270f2b' }}>{nombre}</strong>
          </div>
        </div>

        <div style={{ padding: '0 22px 18px' }}>
          {bloqueo ? (
            <div style={{
              background: '#fff8ed', border: '1px solid #f0d9b8', color: '#a86b1e',
              padding: 12, borderRadius: 8, fontSize: 13, lineHeight: 1.5,
            }}>
              <div style={{ fontWeight: 700, marginBottom: 6 }}>No se puede borrar permanentemente</div>
              Tiene:
              <ul style={{ margin: '6px 0 0 18px', padding: 0 }}>
                {Object.entries(bloqueo.detalle)
                  .filter(([k, v]) => typeof v === 'number' && v > 0)
                  .map(([k, v]) => (
                    <li key={k}>{(DETALLE_HUMANO[k] || ((n) => `${n} ${k}`))(v)}</li>
                  ))}
              </ul>
              <div style={{ marginTop: 10, fontSize: 12 }}>
                Reasigna o archiva esos hijos primero. Borrar permanentemente solo se permite cuando ya no quedan referencias.
              </div>
            </div>
          ) : esPermanente ? (
            <div style={{
              background: '#fef2f2', border: '1px solid #fecaca', color: '#b91c1c',
              padding: 12, borderRadius: 8, fontSize: 13, lineHeight: 1.6,
            }}>
              Esta acción <strong>NO se puede deshacer</strong>. El registro y su historial se eliminarán de la base de datos.
              <div style={{ marginTop: 8 }}>
                Solo procede si estás absolutamente seguro de que es un duplicado o un error que nunca debió existir.
              </div>
            </div>
          ) : (
            <div style={{
              fontSize: 13, color: '#5e666e', lineHeight: 1.5,
              padding: '10px 12px', background: '#f9fafb', borderRadius: 8,
              border: '1px solid #e5e7eb',
            }}>
              Volverá a aparecer en las listas activas y typeaheads.
            </div>
          )}

          {error && (
            <div style={{
              marginTop: 10,
              background: '#fef2f2', border: '1px solid #fecaca', color: '#b91c1c',
              padding: 10, borderRadius: 8, fontSize: 13,
            }}>{error}</div>
          )}
        </div>

        <div style={{
          padding: 16, background: '#fafafa', borderTop: '1px solid #e5e7eb',
          display: 'flex', justifyContent: 'flex-end', gap: 10,
        }}>
          <button
            onClick={onCerrar}
            disabled={enviando}
            style={{
              background: '#fff', color: '#5e666e', border: '1px solid #e5e7eb',
              borderRadius: 8, padding: '10px 16px', fontSize: 13, fontWeight: 600,
              cursor: enviando ? 'not-allowed' : 'pointer', fontFamily: FF,
              opacity: enviando ? 0.5 : 1,
            }}
          >{bloqueo ? 'Entendido' : 'Cancelar'}</button>
          {!bloqueo && (
            <button
              onClick={onConfirmar}
              disabled={enviando}
              style={{
                background: esPermanente ? '#dc2626' : '#16a34a',
                color: '#fff', border: 'none',
                borderRadius: 8, padding: '10px 18px', fontSize: 13, fontWeight: 700,
                cursor: enviando ? 'not-allowed' : 'pointer', fontFamily: FF,
                opacity: enviando ? 0.6 : 1,
              }}
            >{enviando
              ? (esPermanente ? '⟳ Borrando…' : '⟳ Restaurando…')
              : (esPermanente ? 'Sí, borrar permanentemente' : 'Sí, restaurar')}</button>
          )}
        </div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(contenido, document.body);
}
